import React from 'react';
import { Field, useForm } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography } from 'common/typography';
import { useAdCampaignTranslationKeys } from 'modules/campaigns/ad-campaign/hooks/useAdCampaignTranslationKeys';
import { useCampaignModuleContext } from 'modules/campaigns/context';
import { AdCampaignBudgetType } from 'modules/campaigns/types';

import CustomRadioCard from '../../../components/CustomRadioCard';
import { BudgetOption } from '../types';

export type BudgetOptionSelectorProps = {
  name: string;
  budgetOptions: BudgetOption[];
  isDisabled: boolean;
  defaultValue: AdCampaignBudgetType;
  onChange?: (budgetType: AdCampaignBudgetType) => void;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-block-end: 12px;
`;

const InputWrapper = styled.div<{ isDisabled: boolean }>`
  flex: 1;
  display: flex;
  gap: 16px;
  align-items: flex-start;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
  pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'default')};
  cursor: ${({ isDisabled }) => (isDisabled ? 'no-drop' : 'default')};
  width: 100%;
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const BudgetOptionSelector = ({
  name,
  budgetOptions,
  isDisabled,
  defaultValue,
  onChange,
}: BudgetOptionSelectorProps) => {
  const adTranslationKeys = useAdCampaignTranslationKeys();

  const form = useForm();
  const { currency } = useCampaignModuleContext();

  return (
    <Root>
      <Typography.Body3>
        <FormattedMessage id={adTranslationKeys.budgetOptionsDescription} />
      </Typography.Body3>
      <InputWrapper isDisabled={isDisabled}>
        {budgetOptions.map(budgetOption => (
          <Field<AdCampaignBudgetType>
            name={name}
            label={budgetOption.type === 'custom' ? budgetOption.type : budgetOption.value}
            type="radio"
            key={budgetOption.type}
            value={budgetOption.type}
            disabled={isDisabled}
            defaultValue={defaultValue}
          >
            {({ input }) => (
              <CustomRadioCard
                {...input}
                isRecommended={budgetOption.type === 'recommended'}
                onChange={() => {
                  input.onChange(input.value);
                  onChange?.(input.value);
                  if (budgetOption.type !== 'custom') {
                    form.change('budgetAmount', budgetOption.value);
                  }
                }}
              >
                {budgetOption.type === 'custom' ? (
                  <Typography.Body3>
                    <FormattedMessage id="campaigns.ads.budget.custom-budget" />
                  </Typography.Body3>
                ) : (
                  <Typography.Body3>
                    <MonetaryAmount amount={budgetOption.value} currency={currency} />
                  </Typography.Body3>
                )}
              </CustomRadioCard>
            )}
          </Field>
        ))}
      </InputWrapper>
    </Root>
  );
};
